/**
 * Adressen for «Konserter» (`/shows`) og konsertsiden (`/shows/$concertId`). Valideres i kanten,
 * så ingenting lenger inn får en verdi siden ikke har en visning eller fane for.
 */
import type { Action } from '@tvitvi/shared';

import { validateAccessSearch, type AccessSearch } from '@/features/advance-access/access-search';
import type { TKey } from '@/i18n/types';

export const SHOWS_VIEWS = ['list', 'calendar', 'map'] as const;
export type ShowsView = (typeof SHOWS_VIEWS)[number];

export const SHOWS_WHEN = ['upcoming', 'past', 'all'] as const;
export type ShowsWhen = (typeof SHOWS_WHEN)[number];

/** Byråets filter (sak 233): egne konserter, solgte, eller begge. Et rent arrangørhus har det ikke. */
export const SHOWS_KINDS = ['all', 'own', 'sold'] as const;
export type ShowsKind = (typeof SHOWS_KINDS)[number];

export interface ShowsSearch {
  kind?: ShowsKind;
  when?: ShowsWhen;
  view?: ShowsView;
  /** Kalenderens måned, `YYYY-MM`. */
  month?: string;
}

export function validateShowsSearch(search: Record<string, unknown>): ShowsSearch {
  const when = search['when'];
  const view = search['view'];
  const month = search['month'];
  const kind = search['kind'];
  return {
    ...(typeof kind === 'string' && (SHOWS_KINDS as readonly string[]).includes(kind)
      ? { kind: kind as ShowsKind }
      : {}),
    ...(typeof when === 'string' && (SHOWS_WHEN as readonly string[]).includes(when)
      ? { when: when as ShowsWhen }
      : {}),
    ...(typeof view === 'string' && (SHOWS_VIEWS as readonly string[]).includes(view)
      ? { view: view as ShowsView }
      : {}),
    ...(typeof month === 'string' && /^\d{4}-(0[1-9]|1[0-2])$/.test(month) ? { month } : {}),
  };
}

/** Hvem som ser fanen — står ved fanen, så ingen lurer på hva som deles. */
export type ConcertTabAudience = 'all' | 'us' | 'venue' | 'agency';

export interface ConcertTab {
  id: string;
  labelKey: TKey;
  audience: ConcertTabAudience;
  /** Fanen gjelder én artist: den får en artistvelger. */
  perArtist?: boolean;
  permission?: Action;
}

export const CONCERT_TABS = [
  { id: 'overview', labelKey: 'shows.tabs.overview', audience: 'all' },
  { id: 'calendar', labelKey: 'shows.tabs.calendar', audience: 'us' },
  { id: 'people', labelKey: 'shows.tabs.people', audience: 'us' },
  { id: 'resources', labelKey: 'shows.tabs.resources', audience: 'us' },
  { id: 'travel', labelKey: 'shows.tabs.travel', audience: 'agency', perArtist: true },
  { id: 'advance', labelKey: 'shows.tabs.advance', audience: 'all' },
  { id: 'tasks', labelKey: 'shows.tabs.tasks', audience: 'us', perArtist: true },
  {
    id: 'budget',
    labelKey: 'shows.tabs.budget',
    audience: 'us',
    permission: 'concert_budget:read',
  },
  {
    id: 'agreements',
    labelKey: 'shows.tabs.agreements',
    audience: 'us',
    permission: 'concert_budget:read',
  },
  { id: 'documents', labelKey: 'shows.tabs.documents', audience: 'us' },
  { id: 'chat', labelKey: 'shows.tabs.chat', audience: 'agency', perArtist: true },
] as const satisfies readonly ConcertTab[];

export type ConcertTabId = (typeof CONCERT_TABS)[number]['id'];
export const CONCERT_TAB_IDS: readonly ConcertTabId[] = CONCERT_TABS.map((tab) => tab.id);

export interface ShowSearch extends AccessSearch {
  tab?: ConcertTabId;
  /** Artisten de artistbundne fanene gjelder (programpostens id). */
  artist?: string;
  /** «Ny konsert» med «Send bud nå» (QA16 funn 31): budvinduet åpnes for denne artisten. */
  bid?: string;
}

export function validateShowSearch(search: Record<string, unknown>): ShowSearch {
  const tab = search['tab'];
  const artist = search['artist'];
  const bid = search['bid'];
  return {
    ...(typeof tab === 'string' && (CONCERT_TAB_IDS as readonly string[]).includes(tab)
      ? { tab: tab as ConcertTabId }
      : {}),
    ...(typeof artist === 'string' && /^[0-9a-f-]{36}$/i.test(artist) ? { artist } : {}),
    ...(typeof bid === 'string' && /^[0-9a-f-]{36}$/i.test(bid) ? { bid } : {}),
    /* Tilgang-skuffen fra en lenke (ADVANCE-TILGANG.md §6). */
    ...validateAccessSearch(search),
  };
}
